'use client'

import { useState, useRef, useEffect, type ReactNode } from 'react'
import Navbar from '@/components/Navbar'
import HeroBackground from '@/components/HeroBackground'
import Image from 'next/image'
import { getHomepageProjects } from '@/lib/projects'
import ProjectCard from '@/components/ProjectCard'
import ProjectTicker from '@/components/ProjectTicker'
import Pretitle from '@/components/Pretitle'
import ServiceCard from '@/components/ServiceCard'
import DraggableCarousel from '@/components/DraggableCarousel'
import type { ServiceGraphicKey } from '@/components/ServiceGraphics'
import ContactForm from '@/components/ContactForm'
import Footer from '@/components/Footer'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import { motion, useScroll, useTransform } from 'framer-motion'

gsap.registerPlugin(ScrollTrigger)

const services: {
  category: string
  title: string
  description: string
  tags: string[]
  href: string
  graphic: ServiceGraphicKey
}[] = [
  {
    category: 'Design',
    title: 'UX/UI Design',
    description:
      'Product design that ships. Wireframes, design systems, and high-fidelity Figma mockups built for engineering handoff. Branded, responsive, and tested for conversion.',
    tags: ['Wireframes', 'Design Systems', 'Figma', 'Prototyping'],
    href: '/services/ux-ui-design',
    graphic: 'uxui',
  },
  {
    category: 'Engineering',
    title: 'Custom Website & Software Development',
    description:
      'Full-stack web applications, responsive websites, and custom software tailored to your business. From MVP to scale — designed, built, and shipped.',
    tags: ['Web Apps', 'SaaS', 'MVP', 'Full-Stack'],
    href: '/services/custom-development',
    graphic: 'custom',
  },
  {
    category: 'Design',
    title: 'Framer Development',
    description:
      'Pixel-perfect Framer websites with custom interactions, animations, CMS integration, and responsive design. Fast, visual, and conversion-focused.',
    tags: ['Framer Sites', 'CMS', 'Animations', 'No-Code'],
    href: '/services/framer-development',
    graphic: 'framer',
  },
  {
    category: 'Growth',
    title: 'SEO Optimization',
    description:
      'Technical SEO, on-page optimization, structured data, content strategy, and analytics setup — built into the product so search visibility compounds and you can measure what actually works.',
    tags: ['Technical SEO', 'Core Web Vitals', 'Schema', 'Analytics Setup'],
    href: '/services/seo-optimization',
    graphic: 'seo',
  },
  {
    category: 'Automation',
    title: 'AI Integration',
    description:
      'We integrate AI models into your existing product or build AI-powered features from scratch. LLM pipelines, intelligent automation, and seamless embedding.',
    tags: ['LLM Integration', 'AI Features', 'Automation', 'Custom Pipelines'],
    href: '/services/ai-integration',
    graphic: 'ai',
  },
]

// ────────────────────────────────────────────────────────────
// "Why Soleno" benefit cards
// ────────────────────────────────────────────────────────────
// Five short value props rendered in a 2-on-top, 3-on-bottom grid on
// desktop. Icons below are placeholder Lucide-style outlines; they will
// be swapped for the provided 3D-rendered PNG assets once those are
// dropped into /public/images/why-soleno/<slug>.png.
const benefits: {
  slug: string
  title: string
  description: string
  // Populate as each 3D icon asset lands in /public/images/Why us/.
  // When present the <Image> is rendered bare (no background square);
  // when null the placeholder Lucide outline + brand-100 tile is shown.
  iconSrc: string | null
}[] = [
  {
    slug: 'unlimited-revisions',
    title: 'Unlimited Revisions',
    description:
      "We're committed to your satisfaction with unlimited revisions at every step. Our mission is to make your vision come to life exactly as you imagine.",
    iconSrc: '/images/Why us/unlimited-revisons.webp',
  },
  {
    slug: 'lifetime-support',
    title: 'Lifetime Support',
    description:
      "With our lifetime support, you're never alone. We'll be there for you at every stage with necessary guidance and assistance whenever you need it.",
    iconSrc: '/images/Why us/life-time-support.webp',
  },
  {
    slug: 'personalised-plans',
    title: 'Personalised Plans',
    description:
      'Get top-quality service without breaking the bank. Our rates are designed to fit your budget so that you can get the best value for your investment.',
    iconSrc: '/images/Why us/personalized-plans.webp',
  },
  {
    slug: 'custom-design-solutions',
    title: 'Custom Design Solutions',
    description:
      'Our easy payment options are completely flexible. So, you can invest in your success while staying within your budget.',
    iconSrc: '/images/Why us/Custom design solution.webp',
  },
  {
    slug: '24-7-customer-support',
    title: '24/7 Customer Support',
    description:
      'Benefit from the expertise of our carefully chosen resources that are designed to make your journey smooth and effortless with outstanding results.',
    iconSrc: '/images/Why us/24-7-custom-support.webp',
  },
]

// Temporary line-icon placeholders paired with `benefits` by index.
// Replace each entry with <Image src="/images/why-soleno/<slug>.png" />
// once the final 3D icons land in /public.
const BENEFIT_ICONS: ReactNode[] = [
  // Infinity → Unlimited Revisions
  (
    <svg key="i" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.585 0-4.585 8 0 8 5.606 0 7.644-8 12.74-8z" />
    </svg>
  ),
  // Headphones → Lifetime Support
  (
    <svg key="h" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 18v-6a9 9 0 1 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
    </svg>
  ),
  // Sparkles → Personalised Plans
  (
    <svg key="s" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3z" />
      <path d="M5 3v4M3 5h4M19 17v4M17 19h4" />
    </svg>
  ),
  // Palette → Custom Design Solutions
  (
    <svg key="p" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13.5" cy="6.5" r="0.5" fill="currentColor" />
      <circle cx="17.5" cy="10.5" r="0.5" fill="currentColor" />
      <circle cx="8.5" cy="7.5" r="0.5" fill="currentColor" />
      <circle cx="6.5" cy="12.5" r="0.5" fill="currentColor" />
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
    </svg>
  ),
  // MessageCircle → 24/7 Customer Support
  (
    <svg key="m" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22z" />
    </svg>
  ),
]

// Portfolio data lives in `lib/projects.ts` so the home page and the
// dedicated /work page render from the same source.

const testimonials = [
  { name: 'Irfan R.', location: 'United Kingdom', flag: '\u{1F1EC}\u{1F1E7}', text: 'A true professional. Shahid was really patient with the back and forth discussions, and he fully understood our vision. He went above and beyond.', rating: 5 },
  { name: 'Fred G.', location: 'France', flag: '\u{1F1EB}\u{1F1F7}', text: 'Really great work, as always. I have worked with him for several projects and the quality is always the same. Proactive communication that makes the process really easy and smooth.', rating: 5 },
  { name: 'Cameron I.', location: 'United Kingdom', flag: '\u{1F1EC}\u{1F1E7}', text: 'An absolute pleasure to work with. Went above and beyond to deliver exactly what I had envisioned and more. Truly exceeded my expectations.', rating: 5 },
  { name: 'Ylva S.', location: 'Netherlands', flag: '\u{1F1F3}\u{1F1F1}', text: 'An absolutely amazing collaboration. Shahid is incredibly trustworthy and always takes the time for you. He truly went above and beyond to make sure everything was perfect.', rating: 5 },
  { name: 'Adam W.', location: 'United States', flag: '\u{1F1FA}\u{1F1F8}', text: "Sahid's communication, work speed, and attention to detail has been fantastic across multiple projects. Highly recommend.", rating: 5 },
  { name: 'Rez B.', location: 'United Kingdom', flag: '\u{1F1EC}\u{1F1E7}', text: "He took my vision and built a landing page that's clean, fast, and exactly what I needed. Great communication throughout, and the attention to detail was spot on.", rating: 5 },
  { name: 'Thahaseen I.', location: 'India', flag: '\u{1F1EE}\u{1F1F3}', text: "I'm super impressed by Shahid's Framer skills. This was exactly how I wanted. He went above and beyond to make the changes I shared.", rating: 5 },
  { name: 'David C.', location: 'Australia', flag: '\u{1F1E6}\u{1F1FA}', text: 'Shahid truly impressed me with his website design skills! His creativity and attention to detail ensured brand alignment perfectly. The final product exceeded my expectations.', rating: 5 },
  { name: 'Derm V.', location: 'Switzerland', flag: '\u{1F1E8}\u{1F1ED}', text: 'Really great experience. He was patient, responsive, and took the time to understand my vision and brand direction. The result turned out very clean and high quality.', rating: 5 },
  { name: 'Surge Platform', location: 'United States', flag: '\u{1F1FA}\u{1F1F8}', text: "Shahid is the best! I've now worked with him on several projects and have been impressed each time. He has a great design sense and is fluent in the brand vision-to-asset translation process.", rating: 5 },
  { name: 'Burison', location: 'Australia', flag: '\u{1F1E6}\u{1F1FA}', text: 'He delivered a very good and highly interactive but professional landing page. He was honest and showed a deep understanding of what I wanted.', rating: 5 },
  { name: 'Martina M.', location: 'Switzerland', flag: '\u{1F1E8}\u{1F1ED}', text: 'Excellent experience from start to finish. Highly professional, proactive, and communicated clearly throughout the project. The final result exceeded expectations.', rating: 5 },
  { name: "O'Sullivan", location: 'United States', flag: '\u{1F1FA}\u{1F1F8}', text: 'Shahid always fulfills my orders exactly how I ask him to. He is excellent at what he does, always on time, and an extremely good communicator.', rating: 5 },
  { name: 'Lara J.', location: 'United States', flag: '\u{1F1FA}\u{1F1F8}', text: 'Shahid does great design and development work on Framer. I highly recommend him!', rating: 5 },
  { name: 'Joey B.', location: 'United States', flag: '\u{1F1FA}\u{1F1F8}', text: "Great work and great experience. We'll definitely come back for more projects. Highly recommend.", rating: 5 },
  { name: 'Værksted Guru', location: 'Denmark', flag: '\u{1F1E9}\u{1F1F0}', text: 'I had a complicated request for integration of a product builder. Shahid built one from scratch to integrate with the website. I look forward to working more with him.', rating: 5 },
]

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
}

function SplitText({ text, letterClass = 'hero-letter' }: { text: string; letterClass?: string }) {
  // The visible spans split the text into individual letters so the GSAP
  // hooks can animate each character. Without an `aria-label` on the
  // wrapper, screen readers would announce the letters one-by-one
  // ("S-O-L-E-N-O"). Exposing the full string via `aria-label` and
  // hiding the per-character spans restores natural pronunciation.
  //
  // Word spacing: because every char is wrapped in an inline-block span,
  // a regular " " collapses to zero width (CSS white-space collapsing
  // inside inline-blocks). We swap real spaces for a non-breaking space
  // (\u00A0) so word gaps render visually while `aria-label` keeps
  // pronunciation natural for screen readers.
  return (
    <span aria-label={text}>
      {text.split('').map((char, i) => (
        <span key={i} aria-hidden="true" className={`inline-block ${letterClass}`}>
          {char === ' ' ? '\u00A0' : char}
        </span>
      ))}
    </span>
  )
}

// Plain h2 wrapper — GSAP per-letter animation removed at user request.
function AnimatedH2({ children, className }: { children: React.ReactNode; className?: string }) {
  return <h2 className={className}>{children}</h2>
}

// Page background no longer changes based on viewport / section.
// The hero section paints its own dark `<HeroBackground />` on top of the
// fixed light page background, so the rest of the site stays consistently
// light without any scroll-driven cross-fade.
function useScrollBackground() {
  return false
}

const faqs = [
  {
    question: 'What services does Soleno offer as a custom software development studio?',
    answer:
      'Soleno specializes in custom website and software design & development, Framer development, SEO optimization, and AI integration. We build everything from MVPs and SaaS platforms to marketing sites and AI-powered product features.',
  },
  {
    question: 'How much does it cost to build a custom website or web application?',
    answer:
      'Every project is different. A Framer marketing site or an SEO sprint typically starts at a lower investment than a full-stack custom web application. We scope each project individually and provide a clear, fixed quote before work begins — no surprises.',
  },
  {
    question: 'Do you build Framer websites from scratch or use templates?',
    answer:
      'We design and build every Framer site from scratch — no templates. This means custom layouts, interactions, animations, CMS structure, and responsive design that are tailored to your brand and conversion goals.',
  },
  {
    question: 'How does Soleno approach SEO optimization for product companies?',
    answer:
      'We treat SEO as part of engineering, not as a marketing afterthought. That means a full technical audit, Core Web Vitals fixes, proper schema (JSON-LD), search-intent driven content architecture, and migration plans that protect existing rankings. Most engagements are scoped as a 3 or 6-month sprint with clear milestones.',
  },
  {
    question: 'What does AI integration mean and how can it help my product?',
    answer:
      'We integrate AI models — like GPT, Claude, and others — directly into your existing product or build new AI-powered features from scratch. This includes LLM pipelines, intelligent automation, chatbots, content generation, and custom AI workflows tailored to your use case.',
  },
  {
    question: 'How long does it take to build a custom website or software project?',
    answer:
      'Timelines vary by scope. A Framer marketing site typically takes 2–4 weeks. An SEO audit and initial implementation runs 4–6 weeks; ongoing engagements are 3 or 6 months. A custom web application or SaaS MVP usually takes 6–12 weeks. We provide a detailed timeline during the scoping phase so you know exactly what to expect.',
  },
  {
    question: 'Do you work with startups and early-stage founders?',
    answer:
      'Absolutely — startups and founders are our core focus. We understand the pace and constraints of early-stage companies and structure our process around fast iteration, clear communication, and shipping real product quickly.',
  },
  {
    question: 'What is your development process like?',
    answer:
      'We start with a scoping call to understand your goals, then move into design and development in focused sprints. You get regular updates, direct access to the team, and the ability to review and give feedback at every stage. No bloated timelines or unnecessary meetings.',
  },
]

function FAQItem({ question, answer, index, isOpen, onToggle }: { question: string; answer: string; index: number; isOpen: boolean; onToggle: () => void }) {
  return (
    <div
      className="border-b border-charcoal-900/10"
      itemScope
      itemProp="mainEntity"
      itemType="https://schema.org/Question"
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 py-6 md:py-8 text-left"
        aria-expanded={isOpen}
      >
        <h3 itemProp="name" className="text-base md:text-lg font-medium text-charcoal-800 pr-4">{question}</h3>
        <motion.span
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-charcoal-400"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </motion.span>
      </button>
      <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
        <motion.div
          animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
          initial={false}
          transition={{ height: { duration: 0.35, ease: [0.22, 1, 0.36, 1] }, opacity: { duration: 0.25 } }}
          className="overflow-hidden"
        >
          <p className="pb-6 md:pb-8 text-base text-charcoal-400 leading-relaxed max-w-3xl" itemProp="text">
            {answer}
          </p>
        </motion.div>
      </div>
    </div>
  )
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  }

  return (
    <section id="faq" data-bg="#ffffff" className="py-14 md:py-40 px-6 md:px-16">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-[1fr_1.5fr] gap-6 md:gap-20">
        <div>
          <Pretitle className="text-brand-500 mb-5">[ FAQ ]</Pretitle>
          <AnimatedH2 className="text-[clamp(1.75rem,4.5vw,3.25rem)] tracking-[-0.01em] uppercase text-charcoal-800 leading-[1.05]">
            <SplitText text="Common questions" letterClass="h2-letter" />
          </AnimatedH2>
        </div>
        <div>
          {faqs.map((faq, i) => (
            <FAQItem key={i} question={faq.question} answer={faq.answer} index={i} isOpen={openIndex === i} onToggle={() => setOpenIndex(openIndex === i ? null : i)} />
          ))}
        </div>
      </div>
    </section>
  )
}

/**
 * FlipStat — "scramble & settle" reveal for a stat string like "50+",
 * "100%", "3x", "4". When the element scroll-enters the viewport, every
 * digit character in the string rapidly cycles through 0–9 and then
 * locks in left-to-right until the original value is shown. Non-digit
 * characters (+, %, x, …) stay put. Triggered via GSAP ScrollTrigger so
 * it stays in sync with the page's Lenis smooth-scroll system.
 */
function FlipStat({ value, className = '' }: { value: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null)
  const [display, setDisplay] = useState(value)

  useGSAP(
    () => {
      if (!ref.current) return
      const chars = value.split('')
      const digitIdx = chars
        .map((c, i) => (/[0-9]/.test(c) ? i : -1))
        .filter((i) => i !== -1)
      if (digitIdx.length === 0) return

      const randDigit = () => String(Math.floor(Math.random() * 10))
      const scramble = (revealCount: number) =>
        chars
          .map((c, i) => {
            if (i < revealCount) return c
            return digitIdx.includes(i) ? randDigit() : c
          })
          .join('')

      // Park in scrambled state until the trigger fires so the user
      // doesn't see the final value before the animation begins.
      setDisplay(scramble(0))

      const trigger = ScrollTrigger.create({
        trigger: ref.current,
        start: 'top 85%',
        once: true,
        onEnter: () => {
          const duration = 1.1
          const ticks = 22
          let tick = 0
          const interval = window.setInterval(() => {
            tick += 1
            const revealCount = Math.floor((tick / ticks) * chars.length)
            setDisplay(scramble(revealCount))
            if (tick >= ticks) {
              window.clearInterval(interval)
              setDisplay(value)
            }
          }, (duration * 1000) / ticks)
        },
      })

      return () => {
        trigger.kill()
      }
    },
    { scope: ref, dependencies: [value] },
  )

  return (
    <span ref={ref} className={className} style={{ fontVariantNumeric: 'tabular-nums' }}>
      {display}
    </span>
  )
}

function AboutHeading() {
  return (
    <h2 className="text-[clamp(1.75rem,4.5vw,3.25rem)] tracking-[-0.01em] uppercase leading-[1.05] text-center md:text-left">
      <span className="text-white">
        A founder-first studio crafting custom websites, software, and AI-powered products
      </span>{' '}
      <span className="text-white/55">
        designed with precision, built to scale, shipped fast.
      </span>
    </h2>
  )
}

function DraggableRow({ items, rowPrefix, direction = 'left' }: { items: typeof testimonials; rowPrefix: string; direction?: 'left' | 'right' }) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const tweenRef = useRef<gsap.core.Tween | null>(null)
  const dragState = useRef({ dragging: false, startX: 0, startTranslate: 0 })

  const duplicated = [...items, ...items]

  function getX(): number {
    if (!trackRef.current) return 0
    const matrix = new DOMMatrix(getComputedStyle(trackRef.current).transform)
    return matrix.m41
  }

  function wrapX(x: number): number {
    if (!trackRef.current) return x
    const halfW = trackRef.current.scrollWidth / 2
    let wrapped = x % halfW
    if (wrapped > 0) wrapped -= halfW
    return wrapped
  }

  function startAutoScroll(fromX?: number) {
    if (!trackRef.current) return
    tweenRef.current?.kill()
    const el = trackRef.current
    const halfW = el.scrollWidth / 2
    const speed = 40
    const currentX = fromX ?? getX()

    if (direction === 'left') {
      const target = currentX - halfW
      tweenRef.current = gsap.fromTo(el, { x: currentX }, {
        x: target,
        duration: halfW / speed,
        ease: 'none',
        repeat: -1,
        modifiers: { x: (x) => wrapX(parseFloat(x)) + 'px' },
      })
    } else {
      const target = currentX + halfW
      tweenRef.current = gsap.fromTo(el, { x: currentX }, {
        x: target,
        duration: halfW / speed,
        ease: 'none',
        repeat: -1,
        modifiers: { x: (x) => wrapX(parseFloat(x)) + 'px' },
      })
    }
  }

  useGSAP(() => {
    if (!trackRef.current) return
    const halfW = trackRef.current.scrollWidth / 2
    const initialX = direction === 'right' ? -halfW : 0
    gsap.set(trackRef.current, { x: initialX })
    startAutoScroll(initialX)
  }, { scope: wrapperRef })

  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return

    function onPointerDown(e: PointerEvent) {
      dragState.current = { dragging: true, startX: e.clientX, startTranslate: getX() }
      tweenRef.current?.pause()
      el!.setPointerCapture(e.pointerId)
    }

    function onPointerMove(e: PointerEvent) {
      if (!dragState.current.dragging || !trackRef.current) return
      const dx = e.clientX - dragState.current.startX
      gsap.set(trackRef.current, { x: wrapX(dragState.current.startTranslate + dx) })
    }

    function onPointerUp() {
      if (!dragState.current.dragging) return
      dragState.current.dragging = false
      startAutoScroll(getX())
    }

    el.addEventListener('pointerdown', onPointerDown)
    el.addEventListener('pointermove', onPointerMove)
    el.addEventListener('pointerup', onPointerUp)
    el.addEventListener('pointercancel', onPointerUp)
    return () => {
      el.removeEventListener('pointerdown', onPointerDown)
      el.removeEventListener('pointermove', onPointerMove)
      el.removeEventListener('pointerup', onPointerUp)
      el.removeEventListener('pointercancel', onPointerUp)
    }
  })

  return (
    <div ref={wrapperRef} className="relative touch-pan-y">
      <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-[#eeebf3] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-[#eeebf3] to-transparent z-10 pointer-events-none" />
      <div className="overflow-hidden cursor-grab active:cursor-grabbing">
        <div ref={trackRef} className="flex">
          {duplicated.map((t, i) => (
            <div
              key={`${rowPrefix}-${i}`}
              className="flex-shrink-0 w-[260px] sm:w-[360px] mx-3 rounded-2xl border border-charcoal-900/[0.06] bg-white/60 backdrop-blur-md p-7 flex flex-col hover:bg-white/80 transition-colors duration-300 select-none"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex gap-0.5">
                  {Array.from({ length: t.rating }).map((_, s) => (
                    <svg key={s} className="w-5 h-5 text-brand-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span className="text-2xl leading-none" title={t.location}>{t.flag}</span>
              </div>
              <p className="text-base text-charcoal-600 leading-relaxed flex-1">&ldquo;{t.text}&rdquo;</p>
              <div className="mt-5 pt-5 border-t border-charcoal-900/[0.06]">
                <p className="font-display font-semibold text-base text-charcoal-800">{t.name}</p>
                <p className="text-sm text-charcoal-500 mt-0.5">{t.location}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const ICON_PATH = 'M428.09 175.63C426.234 173.773 423.717 172.726 421.09 172.72H318.79L391.14 100.37C392.065 99.4469 392.799 98.3504 393.3 97.1432C393.8 95.9361 394.058 94.6421 394.058 93.3352C394.058 92.0284 393.8 90.7343 393.3 89.5272C392.799 88.32 392.065 87.2234 391.14 86.3002L344.69 39.8502C343.767 38.9253 342.67 38.1915 341.463 37.6908C340.256 37.1902 338.962 36.9325 337.655 36.9325C336.348 36.9325 335.054 37.1902 333.847 37.6908C332.64 38.1915 331.543 38.9253 330.62 39.8502L258.27 112.2V9.9302C258.269 8.62064 258.009 7.32422 257.505 6.11548C257.001 4.90673 256.263 3.80946 255.334 2.88672C254.405 1.96397 253.302 1.23395 252.09 0.738584C250.878 0.243219 249.58 -0.00772431 248.27 0.00018113H182.59C181.285 -0.00113275 179.992 0.25467 178.786 0.752989C177.579 1.25131 176.483 1.98239 175.559 2.90448C174.635 3.82656 173.901 4.9216 173.401 6.12707C172.9 7.33255 172.642 8.62486 172.64 9.9302V112.11L100.37 39.8502C99.4478 38.9256 98.3521 38.192 97.1458 37.6915C95.9394 37.1909 94.6462 36.9333 93.3402 36.9333C92.0341 36.9333 90.741 37.1909 89.5346 37.6915C88.3283 38.192 87.2326 38.9256 86.3102 39.8502L39.8602 86.2902C38.9329 87.2121 38.197 88.3082 37.6948 89.5156C37.1927 90.7229 36.9341 92.0176 36.9341 93.3252C36.9341 94.6328 37.1927 95.9275 37.6948 97.1348C38.197 98.3422 38.9329 99.4383 39.8602 100.36L112.21 172.71H9.95019C8.63895 172.709 7.34039 172.967 6.12933 173.47C4.91827 173.972 3.81863 174.71 2.89377 175.639C1.9689 176.569 1.23709 177.672 0.740477 178.886C0.243864 180.099 -0.00773452 181.399 0.000181133 182.71C-0.0024715 202.78 7.96701 222.029 22.1557 236.223C36.3444 250.418 55.5903 258.395 75.6602 258.4H145.66C157.607 258.4 169.427 260.846 180.393 265.588C191.359 270.329 201.237 277.266 209.42 285.97C210.192 286.808 211.129 287.476 212.172 287.934C213.215 288.391 214.341 288.627 215.48 288.627C216.619 288.627 217.746 288.391 218.789 287.934C219.832 287.476 220.769 286.808 221.54 285.97C229.719 277.266 239.594 270.329 250.556 265.587C261.519 260.846 273.336 258.4 285.28 258.4H355.33C375.4 258.395 394.646 250.418 408.835 236.223C423.023 222.029 430.993 202.78 430.99 182.71C431 181.397 430.748 180.095 430.25 178.879C429.752 177.664 429.018 176.56 428.09 175.63ZM245.84 209.18C238.826 216.185 229.601 220.541 219.735 221.507C209.869 222.473 199.974 219.988 191.735 214.477C183.496 208.965 177.422 200.767 174.549 191.28C171.676 181.793 172.182 171.603 175.979 162.446C179.776 153.29 186.631 145.733 195.375 141.063C204.119 136.394 214.212 134.9 223.933 136.837C233.655 138.774 242.404 144.022 248.691 151.686C254.977 159.351 258.412 168.957 258.41 178.87C258.41 184.501 257.299 190.076 255.142 195.276C252.985 200.477 249.824 205.202 245.84 209.18Z'

export default function Home() {
  const heroSectionRef = useRef<HTMLElement>(null)
  const { scrollYProgress: heroScroll } = useScroll({
    target: heroSectionRef,
    offset: ['start start', 'end start'],
  })
  const heroBgY = useTransform(heroScroll, [0, 1], ['0%', '25%'])
  const heroBgScale = useTransform(heroScroll, [0, 1], [1, 1.1])
  const isDark = useScrollBackground()

  return (
    <main className="relative isolate">
        <div className="fixed inset-0 bg-[#0f0f0f]" aria-hidden="true" style={{ zIndex: -2 }} />
        <div
          className={`fixed inset-0 bg-[#fdfcfb] transition-opacity duration-500 ease-out ${isDark ? 'opacity-0' : 'opacity-100'}`}
          aria-hidden="true"
          style={{ zIndex: -1 }}
        />

        <Navbar />

        {/* ════════════════════════════════════════════ */}
        {/* Hero */}
        {/* ════════════════════════════════════════════ */}
        <section ref={heroSectionRef} data-nav-theme="dark" data-bg="#0f0f0f" className="relative min-h-screen flex items-center overflow-hidden px-6 md:px-16 py-32">
          {/* Three.js "Neural Tide" wave background with embedded Soleno logo */}
          <motion.div
            style={{ y: heroBgY, scale: heroBgScale }}
            className="absolute inset-0 w-full h-full"
            aria-hidden="true"
          >
            <HeroBackground
              spacing={11}
              dotScale={0.7}
              speed={0.6}
              rightBias
              tonedUp
              pureBrand
            />
          </motion.div>

          {/* Soleno logo on the right combining two stacked layers in the
              exact same position:
                1. Backdrop-blur cutout — a div masked to the logo
                   silhouette that blurs whatever is behind it.
                2. Brand purple fill — an SVG of the same logo at 20%
                   opacity layered on top to tint the cutout. */}
          <div
            className="hidden md:block absolute right-[-4%] top-1/2 -translate-y-1/2 z-[5] w-[45vw] max-w-[615px] aspect-[432/289] pointer-events-none"
            aria-hidden="true"
          >
            {/* Layer 1 — masked backdrop blur */}
            <div
              className="absolute inset-0"
              style={{
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                maskImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(
                  `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 432 289' preserveAspectRatio='xMidYMid meet'><path d='${ICON_PATH}' fill='black'/></svg>`,
                )}")`,
                WebkitMaskImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(
                  `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 432 289' preserveAspectRatio='xMidYMid meet'><path d='${ICON_PATH}' fill='black'/></svg>`,
                )}")`,
                maskRepeat: 'no-repeat',
                WebkitMaskRepeat: 'no-repeat',
                maskSize: 'contain',
                WebkitMaskSize: 'contain',
                maskPosition: 'center',
                WebkitMaskPosition: 'center',
              }}
            />
            {/* Layer 2 — brand purple tint at 20% */}
            <svg
              viewBox="0 0 432 289"
              className="absolute inset-0 w-full h-full opacity-10"
              preserveAspectRatio="xMidYMid meet"
            >
              <path d={ICON_PATH} fill="#7D40FF" />
            </svg>
          </div>

          {/* Hero content */}
          <div className="relative z-10 w-full max-w-[1600px] mx-auto">
            {/* Top — label, headline, CTA */}
            <div className="max-w-4xl">
              <Pretitle className="text-brand-400 mb-8">[ Custom Software Development Studio ]</Pretitle>
              <h1
                className="font-display font-light text-white uppercase leading-[1.02] tracking-[-0.01em] text-[clamp(1.75rem,5.8vw,5rem)] mb-10"
              >
                We Design &amp; Build
                <br />
                Products That Ship
              </h1>
              <div>
                <a
                  href="/contact"
                  className="after:hidden inline-flex items-center px-7 py-3.5 rounded-[10px] bg-white text-charcoal-900 font-display font-medium uppercase tracking-wider text-sm hover:bg-white/90 transition-colors duration-300"
                >
                  Start Your Project
                </a>
              </div>
            </div>

            {/* Middle — tags + description */}
            <div
              className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 max-w-3xl"
            >
              <div>
                <p className="text-sm text-white/55 mb-4">We design &amp; ship custom software for</p>
                <div className="flex flex-wrap gap-2">
                  {['MVPs', 'Startups', 'Scale-ups'].map((tag) => (
                    <span
                      key={tag}
                      className="px-4 py-1.5 rounded-full border border-white/15 text-xs md:text-sm text-white/75 hover:border-white/35 hover:text-white transition-colors duration-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <p className="text-sm md:text-base text-white/60 leading-relaxed max-w-md">
                Soleno is a founder-first studio building custom websites, web apps,
                Framer sites, SEO optimization, and AI integrated products designed, built, and shipped fast.
              </p>
            </div>
          </div>

          {/* Bottom — client logo marquee (logos scroll left → right)
              Each logo cell has a right border for the divider effect
              from the reference. Cells are duplicated twice for a
              seamless loop. Logos render in their original colours;
              we only dim them with opacity so each mark stays legible
              on the dark hero (esp. ones that are already white). */}
          <div className="absolute left-0 right-0 bottom-0 z-10 border-t border-white/[0.08] bg-black/30 backdrop-blur-sm overflow-hidden">
            <div className="hero-marquee-track flex items-stretch">
              {[...Array(2)].flatMap((_, dupIdx) =>
                [
                  // `heightClass` lets us optically balance marks whose
                  // intrinsic proportions read too tall against the rest
                  // of the row. Iron's wordmark is visually heavier so
                  // we scale it down ~30% to match the others.
                  { name: 'Magnifiko',     src: '/images/companies/Magnifiko.png' },
                  { name: 'Dr. May',       src: '/images/companies/Dr May.png' },
                  { name: 'Louis Limited', src: '/images/companies/Louis Limited.png', heightClass: 'h-[34px] md:h-[38px]' },
                  { name: 'Iron',          src: '/images/companies/Iron Logo.png',     heightClass: 'h-[16px] md:h-[18px]' },
                  { name: 'Century Tech',  src: '/images/companies/Century Tech.png',  heightClass: 'h-[22px] md:h-[26px]' },
                  { name: 'Conductor Web', src: '/images/companies/Conductor Web.png' },
                  { name: 'Printr',        src: '/images/companies/Printr.png' },
                  { name: 'Prop Edge',     src: '/images/companies/Prop Edge.png' },
                ].map((logo, i) => (
                  <div
                    key={`${dupIdx}-${i}`}
                    className="flex items-center justify-center px-10 py-6 border-r border-white/[0.08] min-w-[180px]"
                  >
                    <Image
                      src={logo.src}
                      alt={`${logo.name} logo`}
                      width={140}
                      height={40}
                      sizes="140px"
                      className={`${logo.heightClass ?? 'h-7 md:h-8'} w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300 select-none`}
                      draggable={false}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════ */}
        {/* Services */}
        {/* ════════════════════════════════════════════ */}
        <section id="services" data-bg="#fdfcfb" className="py-14 md:py-40 px-6 md:px-16">
          <div className="max-w-[1600px] mx-auto">
            <Pretitle className="text-brand-500 mb-5">[ WHAT WE DO ]</Pretitle>
            <AnimatedH2 className="text-[clamp(1.75rem,4.5vw,3.25rem)] tracking-[-0.01em] uppercase text-charcoal-800 leading-[1.05] mb-10 md:mb-16 max-w-3xl">
              <SplitText text="AI-powered development" letterClass="h2-letter" />
              <br />
              <span className="text-charcoal-400">
                <SplitText text="services we offer" letterClass="h2-letter" />
              </span>
            </AnimatedH2>

            {/* Arrow-controlled carousel — single row, fixed-width cards
                that translate via a CSS transition. Prev / next buttons
                step by one card width and disable at the track edges. */}
            <DraggableCarousel gapClassName="gap-5 md:gap-6">
              {services.map((service, i) => (
                <div
                  key={service.title}
                  className="flex-none w-[calc(100vw-3rem)] sm:w-[320px] md:w-[360px] snap-start h-auto"
                >
                  <ServiceCard
                    category={service.category}
                    title={service.title}
                    tags={service.tags}
                    href={service.href}
                    graphic={service.graphic}
                  />
                </div>
              ))}
            </DraggableCarousel>
          </div>
        </section>

        {/* ════════════════════════════════════════════ */}
        {/* Project Screenshots — dual ticker */}
        {/* ════════════════════════════════════════════ */}
        {/* Full-bleed dark section with two infinite ticker rows.
            Top row scrolls left, bottom row scrolls right. Cards are
            full-bleed (no horizontal page padding). Hover (md+) reveals
            a blurred overlay with name, tags and a CTA. On mobile, the
            name + button render below each card instead. */}
        <section
          id="project-screenshots"
          data-bg="#fdfcfb"
          className="bg-[#fdfcfb] py-8 md:py-12"
        >
          <div
            data-bg="#eeebf3"
            className="bg-[#eeebf3] rounded-[20px] md:rounded-[32px] py-11 md:py-32 overflow-hidden"
          >
            <ProjectTicker
              heading={
                <>
                  <Pretitle className="text-brand-500 mb-5">[ PROJECT SCREENSHOTS ]</Pretitle>
                  <AnimatedH2 className="text-[clamp(1.75rem,4.5vw,3.25rem)] tracking-[-0.01em] uppercase text-charcoal-800 leading-[1.05] max-w-3xl">
                    <SplitText text="A glimpse of" letterClass="h2-letter" />
                    <br />
                    <span className="text-charcoal-400">
                      <SplitText text="recent work" letterClass="h2-letter" />
                    </span>
                  </AnimatedH2>
                </>
              }
            />
          </div>
        </section>

        {/* ════════════════════════════════════════════ */}
        {/* Why Soleno */}
        {/* ════════════════════════════════════════════ */}
        {/* Five-card benefit grid: 2 wide cards on top, 3 narrower on
            the bottom (6-col grid on md+, single column on mobile).
            Card styling mirrors the home-page ServiceCard surface
            (#eeebf3 light lavender) so this section sits inside the
            existing brand language. Icons are placeholder Lucide
            outlines until the user supplies finished 3D PNG assets. */}
        <section id="why-soleno" data-bg="#fdfcfb" className="py-14 md:py-40 px-6 md:px-16">
          <div className="max-w-[1600px] mx-auto">
            <Pretitle className="text-brand-500 mb-5">[ WHY SOLENO ]</Pretitle>
            <AnimatedH2 className="text-[clamp(1.75rem,4.5vw,3.25rem)] tracking-[-0.01em] uppercase text-charcoal-800 leading-[1.05] mb-10 md:mb-16 max-w-3xl">
              <SplitText text="Built for founders" letterClass="h2-letter" />
              <br />
              <span className="text-charcoal-400">
                <SplitText text="who ship fast" letterClass="h2-letter" />
              </span>
            </AnimatedH2>

            <div className="grid grid-cols-1 md:grid-cols-6 gap-5 md:gap-6">
              {benefits.map((b, i) => (
                <div
                  key={b.slug}
                  className={`rounded-2xl bg-[#eeebf3] p-6 md:p-8 ${
                    i < 2 ? 'md:col-span-3' : 'md:col-span-2'
                  }`}
                >
                  {b.iconSrc ? (
                    <div className="mb-4 md:mb-5">
                      <Image
                        src={b.iconSrc}
                        alt=""
                        aria-hidden="true"
                        width={80}
                        height={80}
                        className="w-[72px] h-[72px] md:w-20 md:h-20 object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-14 h-14 md:w-16 md:h-16 mb-5 md:mb-6 rounded-2xl bg-brand-100 flex items-center justify-center text-brand-500">
                      {BENEFIT_ICONS[i]}
                    </div>
                  )}
                  <h3 className="font-display font-semibold text-charcoal-800 text-xl md:text-2xl leading-tight mb-3">
                    {b.title}
                  </h3>
                  <p className="text-charcoal-500 text-sm md:text-[15px] leading-relaxed">
                    {b.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════ */}
        {/* Work */}
        {/* ════════════════════════════════════════════ */}
        {/* Outer <section> uses a hard-stop split gradient: top half
            cream (matches Why Soleno above), bottom half lavender
            (matches Testimonials below). The seam at 50% sits behind
            the rounded dark card and is therefore invisible. */}
        <section id="work" className="bg-gradient-to-b from-[#fdfcfb] from-50% to-[#eeebf3] to-50% py-8 md:py-12">
          <div
            data-nav-theme="dark"
            data-bg="#0f0f0f"
            className="bg-charcoal-900 rounded-[20px] md:rounded-[32px] py-11 md:py-32 px-6 md:px-16 overflow-hidden"
          >
            <div className="max-w-[1600px] mx-auto">
              <Pretitle className="text-brand-500 mb-5">[ SELECTED WORK ]</Pretitle>
              <AnimatedH2 className="text-[clamp(1.75rem,4.5vw,3.25rem)] tracking-[-0.01em] uppercase text-white leading-[1.05] mb-6 md:mb-28 max-w-3xl">
                <SplitText text="Projects we're" letterClass="h2-letter" />{' '}
                <br className="hidden md:block" />
                <span className="text-white/40">
                  <SplitText text="proud of" letterClass="h2-letter" />
                </span>
              </AnimatedH2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-16">
                {getHomepageProjects().map((project, i) => (
                  <div key={project.title} className={i % 2 === 1 ? 'md:mt-24' : ''}>
                    <ProjectCard project={project} dark />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════ */}
        {/* Testimonials */}
        {/* ════════════════════════════════════════════ */}
        <section data-bg="#eeebf3" className="relative bg-[#eeebf3] py-14 md:py-40 overflow-hidden">

          <div className="relative z-10 max-w-[1600px] mx-auto">
            <div className="px-6 md:px-16 mb-6 md:mb-20">
              <Pretitle className="text-brand-500 mb-5">[ TESTIMONIALS ]</Pretitle>
              <AnimatedH2 className="text-[clamp(1.75rem,4.5vw,3.25rem)] tracking-[-0.01em] uppercase text-charcoal-800 leading-[1.05] max-w-3xl">
                <SplitText text="Trusted by founders" letterClass="h2-letter" />
                <br />
                <span className="text-charcoal-400">
                  <SplitText text="worldwide" letterClass="h2-letter" />
                </span>
              </AnimatedH2>
            </div>

            <div className="flex flex-col gap-5">
              {[testimonials.slice(0, 8), testimonials.slice(8)].map((row, rowIdx) => (
                <DraggableRow key={rowIdx} items={row} rowPrefix={`r${rowIdx + 1}`} direction={rowIdx === 0 ? 'left' : 'right'} />
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════ */}
        {/* About */}
        {/* ════════════════════════════════════════════ */}
        {/* Outer <section> uses a hard-stop split gradient: top half
            lavender (matches Testimonials above), bottom half cream
            (matches FAQ below). The seam at 50% sits behind the
            rounded dark card and is therefore invisible. */}
        <section id="about" className="bg-gradient-to-b from-[#eeebf3] from-50% to-[#fdfcfb] to-50% py-8 md:py-12">
          <div
            data-nav-theme="dark"
            data-bg="#0f0f0f"
            className="relative bg-charcoal-900 rounded-[20px] md:rounded-[32px] py-11 md:py-32 px-6 md:px-16 overflow-hidden"
          >
            <div className="relative z-10 max-w-[1600px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-[30%_70%] gap-6 md:gap-16 mb-10">
              {/* Founder profile block: section pretitle, then headshot
                  (the same image used in the nav "Book a call" pill),
                  then name + role. Stacks centered on mobile, anchors
                  to the left on md+. */}
              <div className="flex flex-col items-center md:items-start text-center md:text-left">
                <Pretitle className="text-white/60 pt-2 mb-8">[ ABOUT SOLENO ]</Pretitle>
                <Image
                  src="/images/linkedin-profile.webp"
                  alt="Shahid Hasan, CEO & Founder of Soleno"
                  width={188}
                  height={188}
                  className="w-[146px] h-[146px] md:w-[166px] md:h-[166px] rounded-2xl object-cover border-2 border-brand-500 mb-5"
                  draggable={false}
                />
                <p className="font-display font-semibold text-base md:text-xl text-white leading-tight">
                  Shahid Hasan
                </p>
                <p className="text-sm md:text-base text-white/60 mt-1">
                  CEO &amp; Founder
                </p>
              </div>
              <div className="md:pr-16">
                <AboutHeading />
                <div className="flex flex-wrap justify-center md:justify-start gap-4 mt-10">
                  <a
                    href="/contact"
                    className="after:hidden inline-flex items-center px-7 py-3.5 rounded-[10px] bg-brand-500 text-white font-display font-medium uppercase tracking-wider text-sm hover:bg-brand-600 transition-colors duration-300"
                  >
                    Get in Touch
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-24 md:mt-32 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 border-t border-white/15 pt-16">
              {[
                { stat: '50+', label: 'Projects delivered' },
                { stat: '3x', label: 'Faster delivery' },
                { stat: '100%', label: 'Founder-led' },
                { stat: '4', label: 'Core services' },
              ].map((item) => (
                <div key={item.label}>
                  <p className="font-display text-3xl md:text-4xl font-medium text-white mb-2">
                    <FlipStat value={item.stat} />
                  </p>
                  <p className="text-sm text-white/60">{item.label}</p>
                </div>
              ))}
            </div>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════ */}
        {/* FAQ */}
        {/* ════════════════════════════════════════════ */}
        <FAQSection />

        {/* ════════════════════════════════════════════ */}
        {/* Contact / CTA */}
        {/* ════════════════════════════════════════════ */}
        <section id="contact" data-bg="#ffffff" className="py-14 md:py-40 px-6 md:px-16">
          <div className="max-w-[1600px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-0 rounded-3xl overflow-hidden">

              {/* Left — CTA Card */}
              <div className="lg:w-[50%] bg-[#09080f] text-white px-8 md:px-14 py-16 md:py-20 flex flex-col justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.04] via-transparent to-[#09080f] pointer-events-none" />
                <div className="relative z-10">
                  <Pretitle className="text-brand-400 mb-6">[ START A PROJECT ]</Pretitle>
                  <h2 className="text-[clamp(1.75rem,4vw,2.75rem)] tracking-[-0.01em] uppercase leading-[1.05] mb-4">
                    Let&apos;s build your
                    <br />
                    next project.
                  </h2>
                  <p className="text-base md:text-lg text-white/60 mb-6 max-w-md leading-relaxed">
                    Clear. Structured. Efficient. Book a free consultation and let&apos;s talk about your vision.
                  </p>
                  <p className="text-xs text-white/20">
                    Response within 24h &middot; Free consultation
                  </p>
                </div>
              </div>

              {/* Right — Contact Form */}
              <ContactForm className="lg:w-[50%]" />

            </div>
          </div>
        </section>

        <Footer />

      </main>
  )
}
